import React, { useState } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';

const SelfAssessmentModal = ({ onClose }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  const questions = [
    {
      id: 'q1',
      question: 'Over the past 2 weeks, how often have you felt down, depressed, or hopeless?',
      options: [
        { value: 0, label: 'Not at all' },
        { value: 1, label: 'Several days' },
        { value: 2, label: 'More than half the days' },
        { value: 3, label: 'Nearly every day' }
      ]
    },
    {
      id: 'q2',
      question: 'Over the past 2 weeks, how often have you had little interest or pleasure in doing things?',
      options: [
        { value: 0, label: 'Not at all' },
        { value: 1, label: 'Several days' },
        { value: 2, label: 'More than half the days' },
        { value: 3, label: 'Nearly every day' }
      ]
    },
    {
      id: 'q3',
      question: 'How often have you felt nervous, anxious, or on edge?',
      options: [
        { value: 0, label: 'Not at all' },
        { value: 1, label: 'Several days' },
        { value: 2, label: 'More than half the days' },
        { value: 3, label: 'Nearly every day' }
      ]
    },
    {
      id: 'q4',
      question: 'How often have you been unable to stop or control worrying?',
      options: [
        { value: 0, label: 'Not at all' },
        { value: 1, label: 'Several days' },
        { value: 2, label: 'More than half the days' },
        { value: 3, label: 'Nearly every day' }
      ]
    },
    {
      id: 'q5',
      question: 'How often have you had trouble falling asleep, staying asleep, or sleeping too much?',
      options: [
        { value: 0, label: 'Not at all' },
        { value: 1, label: 'Several days' },
        { value: 2, label: 'More than half the days' },
        { value: 3, label: 'Nearly every day' }
      ]
    },
    {
      id: 'q6',
      question: 'How often have you felt tired or had little energy?',
      options: [
        { value: 0, label: 'Not at all' },
        { value: 1, label: 'Several days' },
        { value: 2, label: 'More than half the days' },
        { value: 3, label: 'Nearly every day' }
      ]
    },
    {
      id: 'q7',
      question: 'How much have these difficulties affected your work, relationships, or daily activities?',
      options: [
        { value: 0, label: 'Not at all' },
        { value: 1, label: 'Somewhat' },
        { value: 2, label: 'Quite a bit' },
        { value: 3, label: 'Extremely' }
      ]
    }
  ];

  const handleAnswer = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleNext = () => {
    if (step < questions.length - 1) {
      setStep(step + 1);
    } else {
      calculateResults();
    }
  };

  const handlePrevious = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const calculateResults = () => {
    setShowResults(true);
  };

  const getTotalScore = () => {
    return Object.values(answers).reduce((sum, val) => sum + val, 0);
  };

  const getRecommendation = () => {
    const score = getTotalScore();
    
    if (score <= 5) {
      return {
        level: 'Minimal',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        icon: <CheckCircle className="w-12 h-12 text-green-600" />,
        message: 'Your responses suggest minimal symptoms of depression or anxiety.',
        recommendation: 'Continue practicing good self-care habits including regular exercise, healthy eating, adequate sleep, and maintaining social connections. If symptoms worsen, don\'t hesitate to reach out for support.'
      };
    } else if (score <= 10) {
      return {
        level: 'Mild',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        icon: <AlertCircle className="w-12 h-12 text-yellow-600" />,
        message: 'Your responses suggest mild symptoms that may benefit from support.',
        recommendation: 'Consider speaking with a mental health professional to discuss your concerns. Early intervention can be very effective. We offer individual therapy and support services that may help.'
      };
    } else if (score <= 15) {
      return {
        level: 'Moderate',
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        icon: <AlertCircle className="w-12 h-12 text-orange-600" />,
        message: 'Your responses suggest moderate symptoms that would benefit from professional support.',
        recommendation: 'We recommend scheduling an appointment with one of our licensed clinicians. Treatment options like therapy and/or medication management can significantly improve your wellbeing.'
      };
    } else {
      return {
        level: 'Severe',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        icon: <AlertCircle className="w-12 h-12 text-red-600" />,
        message: 'Your responses suggest severe symptoms. Professional help is strongly recommended.',
        recommendation: 'Please contact us immediately to schedule an urgent appointment. If you\'re in crisis, call 988 (Suicide & Crisis Lifeline) or visit your nearest emergency room. You don\'t have to face this alone - help is available.'
      };
    }
  };

  const renderQuestion = () => {
    const currentQuestion = questions[step];
    const currentAnswer = answers[currentQuestion.id];

    return (
      <div className="space-y-6">
        <div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-gray-500">
              Question {step + 1} of {questions.length}
            </span>
            <div className="flex gap-1">
              {questions.map((q, idx) => (
                <div
                  key={q.id || `q-${idx}`}
                  className={`h-2 w-8 rounded-full ${
                    idx === step ? 'bg-blue-600' : idx < step ? 'bg-blue-300' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-6">
            {currentQuestion.question}
          </h3>
        </div>

        <div className="space-y-3">
          {currentQuestion.options.map((option) => (
            <button
              key={option.value}
              onClick={() => handleAnswer(currentQuestion.id, option.value)}
              className={`w-full text-left px-6 py-4 rounded-lg border-2 transition-all ${
                currentAnswer === option.value
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
              }`}
              data-testid={`assessment-option-${option.value}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">{option.label}</span>
                {currentAnswer === option.value && (
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                )}
              </div>
            </button>
          ))}
        </div>

        <div className="flex gap-4 pt-6">
          {step > 0 && (
            <button
              onClick={handlePrevious}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
            >
              <ArrowLeft className="w-5 h-5" />
              Previous
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={currentAnswer === undefined}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed font-semibold"
            data-testid="assessment-next-btn"
          >
            {step === questions.length - 1 ? 'View Results' : 'Next'}
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    const result = getRecommendation();
    const score = getTotalScore();

    return (
      <div className="space-y-6 text-center">
        <div className="flex justify-center mb-6">
          {result.icon}
        </div>

        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">
            Assessment Complete
          </h3>
          <p className={`text-lg font-semibold ${result.color}`}>
            {result.level} Symptoms Indicated
          </p>
          <p className="text-sm text-gray-500 mt-2">Score: {score} out of 21</p>
        </div>

        <div className={`${result.bgColor} rounded-lg p-6 text-left`}>
          <p className="text-gray-800 mb-4">
            {result.message}
          </p>
          <p className="text-gray-700">
            <strong>Recommendation:</strong> {result.recommendation}
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
          <p className="text-sm text-gray-700">
            <strong>Important Note:</strong> This self-assessment is not a diagnostic tool. 
            It's designed to help you understand if you might benefit from speaking with a mental health professional. 
            Only a qualified clinician can provide a proper diagnosis and treatment plan.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <button
            onClick={() => {
              setStep(0);
              setAnswers({});
              setShowResults(false);
            }}
            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
          >
            Retake Assessment
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            data-testid="assessment-close-btn"
          >
            Schedule Appointment
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="self-assessment-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Mental Health Self-Assessment</h2>
            {!showResults && (
              <p className="text-sm text-gray-600 mt-1">
                This brief assessment can help identify if you might benefit from professional support
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            data-testid="self-assessment-close-btn"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {showResults ? renderResults() : renderQuestion()}
        </div>
      </div>
    </div>
  );
};

export default SelfAssessmentModal;
